import { useState } from 'react'
import {
  downloadCertificateFile,
  extractCertificateType,
} from '../utils/certificate-download'

// Simplified type for manager context - accepts any object with certificateID
interface CertificateItem {
  certificateID: string | null
  type?: string
  courseActivity?: string
}

export function useCertificateDownloadManager() {
  let [downloadingCerts, setDownloadingCerts] = useState<Set<string>>(new Set())

  let downloadCertificate = async (
    item: CertificateItem,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!item.certificateID)
      return { success: false, error: 'No certificate ID' }

    let certID = item.certificateID
    setDownloadingCerts((prev) => new Set(prev).add(certID))

    try {
      // Extract certificate type from type field
      // Handles formats like "External - Live", "External - Recorded", "Live", "Recorded", etc.
      let certificateType = extractCertificateType(item.type)

      let filename = item.courseActivity
        ? `${item.courseActivity.replace(/\s+/g, '_')}_${certID}.pdf`
        : `${certID}.pdf`

      let result = await downloadCertificateFile(
        certID,
        certificateType,
        filename,
      )

      return result
    } catch (err: any) {
      return {
        success: false,
        error: `Certificate download failed: ${err?.message || 'Unknown error'}`,
      }
    } finally {
      setDownloadingCerts((prev) => {
        let newSet = new Set(prev)
        newSet.delete(certID)
        return newSet
      })
    }
  }

  let isDownloading = (certificateID: string | null) =>
    certificateID ? downloadingCerts.has(certificateID) : false

  return {
    downloadCertificate,
    isDownloading,
  }
}
